import { type Application, applications } from '@labeling/globals';
import { Button, Combobox, Modal, TextField } from '@lego/connect-components-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { TableSkeleton } from '@/components/table-skeleton';
import { Table } from '../../components';
import type { TableColumn } from '../../components/table';
import { useApi } from '../../providers/api';
import { useToastContext } from '../../providers/toast';
import { useUserStore } from '../../stores/user-store';
import type { LabelAssignmentData, LabelData } from '../../types/types';
import { fetchAllPages } from '../../utils/pagination';
import { canEditLabelAssignments } from '../../utils/permissions';
import { assertResponseOk, assertSettledResponsesOk, toErrorMessage } from '../../utils/response';

export const LabelsPage = () => {
  const { t } = useTranslation();
  const { client } = useApi();
  const { user, isAdminOrMaintainer } = useUserStore();
  const [selectedLabel, setSelectedLabel] = useState<LabelData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);
  const { showToast } = useToastContext();
  const [editForm, setEditForm] = useState({
    description: '',
    assignments: [] as string[],
  });
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);

  const {
    data: labels,
    isLoading: labelsIsLoading,
    mutate: reloadLabels,
  } = useSWR('labels', () =>
    fetchAllPages((query) => client.v1.labels.$get({ query }).then((r) => r.json()))
  );

  const {
    data: labelAssignments,
    isLoading: assignmentsIsLoading,
    mutate: reloadAssignments,
  } = useSWR('label-assignments', () =>
    fetchAllPages((query) => client.v1['label-assignments'].$get({ query }).then((r) => r.json()))
  );

  const revisionOptions = useMemo(
    () =>
      selectedLabel?.availableRevisions?.map((rev) => ({
        value: rev,
        label: rev,
      })) ?? [],
    [selectedLabel]
  );

  const assignmentOptions = useMemo(
    () =>
      applications.map((app) => ({
        value: app,
        label: app,
      })),
    []
  );

  const handleLabelClick = (label: LabelData) => {
    triggerRef.current = document.activeElement as HTMLElement;
    setSelectedLabel(label);
    const assignments = getLabelAssignments(label.id!);
    setEditForm({
      description: label.description || '',
      assignments: assignments.map((a) => a.application),
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleSave = async () => {
    if (!selectedLabel || !isAdminOrMaintainer()) return;
    const labelId = selectedLabel.id!;
    const failedMessage = t('labelsPage.response.labelUpdateFailed');
    const refreshCaches = () => Promise.all([reloadLabels(), reloadAssignments()]);

    try {
      await assertResponseOk(
        await Promise.all([
          client.v1.labels[':id']['active-revisions'].$put({
            param: { id: String(labelId) },
            json: { activeRevisions: selectedLabel.activeRevisions || [] },
          }),
          client.v1.labels[':id'].$put({
            param: { id: String(labelId) },
            json: { description: editForm.description },
          }),
        ]),
        failedMessage
      );

      if (canEditLabelAssignments(user)) {
        const currentAssignments = getLabelAssignments(labelId);
        const newAssignments = editForm.assignments.filter(
          (app) => !currentAssignments.some((a) => a.application === app)
        );
        const removedAssignments = currentAssignments.filter(
          (a) => !editForm.assignments.includes(a.application)
        );

        await assertSettledResponsesOk(
          [
            ...newAssignments.map((app) =>
              client.v1['label-assignments'].$post({
                json: { labelId, application: app as Application },
              })
            ),
            ...removedAssignments.map((assignment) =>
              client.v1['label-assignments'][':id'].$delete({
                param: { id: assignment.id.toString() },
              })
            ),
          ],
          failedMessage
        );
      }

      await refreshCaches();
      showToast(t('labelsPage.response.labelUpdatedSuccess'), 'success');
      handleCloseDialog();
    } catch (error) {
      showToast(toErrorMessage(error, failedMessage), 'error');
      // Partial saves may have succeeded; refresh so a retry doesn't repeat them.
      await refreshCaches().catch(() => undefined);
    }
  };

  const getLabelAssignments = useCallback(
    (labelId: number) => {
      if (!labelAssignments?.items) return [];
      return labelAssignments.items.filter(
        (assignment: LabelAssignmentData) => assignment.labelId === labelId
      );
    },
    [labelAssignments?.items]
  );

  const formatAssignments = useCallback(
    (labelId: number) => {
      const assignments = getLabelAssignments(labelId);
      if (assignments.length === 0) return '-';
      return assignments
        .map((assignment: LabelAssignmentData) => assignment.application)
        .join(', ');
    },
    [getLabelAssignments]
  );

  const tableColumns: TableColumn<LabelData>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('labelsPage.display.name'),
        sortable: true,
        render: (value) => <span className="text-core-slate-1300 font-medium">{value}</span>,
      },
      {
        key: 'description',
        label: t('labelsPage.display.description'),
        sortable: true,
        accessor: (label: LabelData) => label.description || '-',
      },
      {
        key: 'path',
        label: t('labelsPage.display.path'),
        sortable: true,
        accessor: (label: LabelData) => label.externalPath,
      },
      {
        key: 'id',
        label: 'ID',
        sortable: true,
        accessor: (label: LabelData) => label.id?.toString() ?? '-',
      },
      {
        key: 'availableRevisions',
        label: t('labelsPage.display.revisions'),
        sortable: true,
        accessor: (label: LabelData) => label.availableRevisions?.length.toString() || '0',
      },
      {
        key: 'applications',
        label: t('labelsPage.display.applications'),
        sortable: false,
        accessor: (label: LabelData) => formatAssignments(label.id!),
      },
      {
        key: 'created',
        label: t('labelsPage.display.created'),
        sortable: true,
        accessor: (label: LabelData) =>
          label.createdAt ? new Date(label.createdAt).toLocaleDateString() : '-',
      },
      {
        key: 'updated',
        label: t('labelsPage.display.updated'),
        sortable: true,
        accessor: (label: LabelData) =>
          label.updatedAt ? new Date(label.updatedAt).toLocaleDateString() : '-',
      },
    ],
    [formatAssignments, t]
  );

  const isLoading = labelsIsLoading || assignmentsIsLoading;

  if (!labels || !labelAssignments || isLoading) {
    return <TableSkeleton columns={tableColumns.length} page showAction />;
  }

  return (
    <>
      <div className="px-400 py-400">
        <h1 className="text-heading-md font-brand font-bold text-core-slate-1300 mb-300">
          {`${t('labelsPage.display.labelsTitle')} (${labels.itemCount})`}
        </h1>

        <Table
          data={labels.items}
          columns={tableColumns}
          onRowClick={handleLabelClick}
          loading={isLoading}
          emptyMessage={t('labelsPage.display.noLabelsFound')}
          searchPlaceholder={t('labelsPage.display.searchLabelsPlaceholder')}
          ariaLabel={t('labelsPage.display.labelsTitle')}
        />
      </div>

      <Modal
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        triggerRef={triggerRef}
        size="medium"
      >
        <Modal.Title>
          {selectedLabel
            ? isAdminOrMaintainer()
              ? t('labelsPage.action.editLabelTitle', { name: selectedLabel.name })
              : t('labelsPage.action.viewLabelTitle', { name: selectedLabel.name })
            : ''}
        </Modal.Title>
        <Modal.Content>
          {selectedLabel && (
            <div
              ref={setPortalTarget}
              className="grid grid-cols-1 sm:grid-cols-2 gap-200 text-body-xs"
            >
              <div className="sm:col-span-2">
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('labelsPage.display.name')}:
                </span>
                <TextField
                  value={selectedLabel.name}
                  placeholder={t('labelsPage.display.labelNamePlaceholder')}
                  size="medium"
                  disabled
                  className="opacity-60"
                />
              </div>

              <div className="sm:col-span-2">
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('labelsPage.display.description')}:
                </span>
                <TextField
                  value={editForm.description}
                  onChange={(_e, value) => setEditForm({ ...editForm, description: value })}
                  placeholder={t('labelsPage.display.descriptionPlaceholder')}
                  size="medium"
                  disabled={!isAdminOrMaintainer()}
                />
              </div>

              <div className="max-w-full break-words">
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('labelsPage.display.pathLabel')}
                </span>
                <p className="text-core-slate-1300">{selectedLabel.externalPath}</p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('labelsPage.display.revisionsLabel')}
                </span>
                <Combobox
                  fullWidth
                  value={selectedLabel.activeRevisions ?? []}
                  onChange={(_e, items) =>
                    setSelectedLabel((label) =>
                      label ? { ...label, activeRevisions: items.map((i) => i.value) } : label
                    )
                  }
                  placeholder="Select revisions"
                  size="medium"
                  disabled={!revisionOptions.length}
                  multiple
                  {...(portalTarget ? { portalTarget } : {})}
                >
                  {revisionOptions.map((o) => (
                    <Combobox.Option key={o.value} value={o.value}>
                      {o.label}
                    </Combobox.Option>
                  ))}
                </Combobox>
              </div>
              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('labelsPage.display.applicationsLabel')}
                </span>
                <Combobox
                  fullWidth
                  value={editForm.assignments}
                  onChange={(_e, items) =>
                    setEditForm({ ...editForm, assignments: items.map((i) => i.value) })
                  }
                  placeholder={t('labelsPage.display.selectApplications')}
                  size="medium"
                  disabled={!isAdminOrMaintainer()}
                  multiple
                  {...(portalTarget ? { portalTarget } : {})}
                >
                  {assignmentOptions.map((o) => (
                    <Combobox.Option key={o.value} value={o.value}>
                      {o.label}
                    </Combobox.Option>
                  ))}
                </Combobox>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('labelsPage.display.createdLabel')}
                </span>
                <p className="text-core-slate-1300">
                  {selectedLabel.createdAt
                    ? new Date(selectedLabel.createdAt).toLocaleDateString()
                    : '-'}
                </p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('labelsPage.display.updatedLabel')}
                </span>
                <p className="text-core-slate-1300">
                  {selectedLabel.updatedAt
                    ? new Date(selectedLabel.updatedAt).toLocaleDateString()
                    : '-'}
                </p>
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Footer>
          <Button variant="neutral" size="small" onClick={handleCloseDialog}>
            {isAdminOrMaintainer() ? t('labelsPage.action.cancel') : t('labelsPage.action.close')}
          </Button>
          {isAdminOrMaintainer() && (
            <Button variant="primary" size="small" onClick={handleSave}>
              {t('labelsPage.action.saveChanges')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
